import { em, percent, px, rem, rotate } from 'csx';
import React, { ReactElement, ReactChild, ReactNode, ReactFragment } from 'react';
import ReactCSSTransitionGroup from 'react-addons-css-transition-group';
import { classes, style } from 'typestyle';
import { colorGreyAccent } from '../../theme/color';
import { transitionExpandFade } from '../../theme/transition';
import styles from './styles';
import { SVGIcon } from '../svg-icon';
import { centerCenter } from 'csstips';
import { shadowElevation2 } from '../../theme/shadow';

interface CollapsibleProps {
  initial?: boolean;
  overlay?: boolean;
  className?: string;
  wrapperClassName?: string;
  show?: boolean;
}

interface SummaryProps {
  indicator?: boolean;
  className?: string;
  children: ReactNode | ReactNode[];
  onToggle?: (event: any, ref?: any) => void;
}

interface StateIndicatorProps {
  className?: string;
}

class StateIndicator extends React.Component<StateIndicatorProps> {
  render() {
    const { className } = this.props;

    return (
      <div className={classes(styles.stateIndicatorWrapper, className)}>
        <span className={styles.stateIndicatorButton}>
          <SVGIcon.ArrowDown color="black" size={16} />
        </span>
      </div>
    );
  }
}

class Summary extends React.Component<SummaryProps> {
  ref: null | HTMLElement = null;

  render() {
    const { children, indicator, className } = this.props;

    return (
      <div
        ref={(el) => (this.ref = el)}
        className={classes('summary', className, styles.summary)}
        tabIndex={0}
        onClick={this.clickToggle.bind(this)}
        onKeyDown={this.keyToggle.bind(this)}
      >
        {children}
        {indicator && <StateIndicator />}
      </div>
    );
  }

  baseToggle(evt, ref) {
    if (typeof this.props.onToggle === 'function') {
      this.props.onToggle(evt, ref);
    }
  }

  clickToggle(evt, ref) {
    this.ref.blur();
    this.baseToggle(evt, ref);
  }

  keyToggle(evt, ref) {
    const keys = [32, 13];
    const isKey = (key) => evt.keyCode === key;
    if (keys.some(isKey)) {
      evt.preventDefault();
      this.baseToggle(evt, ref);
    }
  }
}

export class Collapsible extends React.Component<CollapsibleProps, { show: boolean }> {
  static styles = styles;

  static Summary = Summary;

  static StateIndicator = StateIndicator;

  constructor(props: CollapsibleProps) {
    super(props);

    this.state = {
      show: 'initial' in props ? props.initial : false,
    };
  }

  toggle(evt, ref) {
    this.setState({
      show: !this.state.show,
    });
  }

  render() {
    const { children, className, wrapperClassName, show: parentShow } = this.props;
    const { show: childShow } = this.state;

    const show = typeof parentShow === 'boolean' ? parentShow : childShow;

    const toggle = this.toggle.bind(this);

    let summary: ReactElement<SummaryProps> = <Summary onToggle={toggle}>Show more</Summary>;
    let details = children;
    if (Array.isArray(children)) {
      const userSummary: ReactElement<SummaryProps> = children.find(
        (el) => typeof el === 'object' && 'type' in el && (el as React.ReactElement<any>).type === Summary, // Old TS version can't infer type from conditional
      ) as ReactElement | undefined;
      if (userSummary) {
        if (typeof userSummary.props.onToggle !== 'function') {
          summary = React.cloneElement(userSummary, { ...userSummary.props, onToggle: toggle } as SummaryProps);
        } else {
          summary = userSummary;
        }
        details = children.filter((el) => el !== userSummary);
      }
    }

    return (
      <div className={classes('details', show ? 'open' : 'closed', styles.container, className)}>
        {summary}
        <ReactCSSTransitionGroup {...transitionExpandFade()}>{show && <div>{details}</div>}</ReactCSSTransitionGroup>
      </div>
    );
  }
}
